import { Grid, Typography } from '@mui/material'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useOutletContext } from 'react-router'
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import FormControlRadioBoolean from 'components/FormControlRadioBoolean'
import { useAuthContext } from 'contexts/auth.context'
import { Funcionalidades } from 'utils/helpers/funcionalidades'
import { useMeioAbertoDadosProcessuais } from 'hooks/meio-aberto-dados-processuais.hook'
import {
  SocioeducationMeasureProceduralDataDefaultValues,
  SocioeducationMeasureProceduralDataSchema,
  SocioeducationMeasureProceduralDataType,
} from 'types/socioeducation-measure-procedural-data.type'
import { StatusMeasureList } from '../components/StatusMeasureList'
import FormControlRadio from 'components/FormControlRadio'
import { useSnackBar } from 'contexts/snackbar.context'
import { useInputSelectProceduralData } from 'pages/CreateHearing/components/StepProceduralData/hooks/select-option.hook'
import InputMask from 'components/FormInputMask'

export const simNaoOptions = [
  {
    label: 'Sim',
    value: true,
  },
  {
    label: 'Não',
    value: false,
  },
]

export const tipoRemissaoOptions = [
  {
    label: 'Suspensiva',
    value: 'SUSPENSIVA',
  },
  {
    label: 'Extintiva',
    value: 'EXTINTIVA',
  },
]

export const medidaAplicadaOptions = [
  {
    label: 'LA',
    value: 'LA',
  },
  {
    label: 'PSC',
    value: 'PSC',
  },
  {
    label: 'LA+PSC',
    value: 'LA_PSC',
  },
]

export const medidaRestritivaOptions = [
  {
    label: 'Internação Provisória',
    value: 'INTERNACAO_PROVISORIA',
  },
  {
    label: 'Internação',
    value: 'INTERNACAO',
  },
  {
    label: 'Semiliberdade',
    value: 'SEMILIBERDADE',
  },
]

type OutletContextType = {
  currentStep: number
  formRefs: MutableRefObject<Record<number, { submit: () => Promise<void> | void }>>
  setPessoaFisicaId: Dispatch<SetStateAction<number | null | undefined>>
}

export const ProceduralDataPage = () => {
  const { id } = useParams()
  const { handleHasPermission } = useAuthContext()
  const { currentStep, formRefs } = useOutletContext<OutletContextType>()
  const methods = useForm<SocioeducationMeasureProceduralDataType>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(SocioeducationMeasureProceduralDataSchema),
    defaultValues: SocioeducationMeasureProceduralDataDefaultValues,
  })
  const { dadosProcessuais, handleSalvar: handleSalvarMeioAberto } =
    useMeioAbertoDadosProcessuais(id)
  const { measureStatusLaPscOptions, measureStatusOptions } = useInputSelectProceduralData()

  const {
    handleSubmit,
    clearErrors,
    control,
    reset,
    watch,
    setError,
    setValue,
    formState: { isDirty, dirtyFields },
  } = methods

  const remissaoWatch = watch('remissao.trataRemissao')
  const processoVemseWatch = watch('processoVemse')
  const processoVijWatch = watch('processoVij')
  const doNotHasEditPermission = !handleHasPermission(Funcionalidades.meioAberto.editar)
  const hasDirtyFields = Object.keys(dirtyFields).length > 0 && dirtyFields.constructor === Object
  const { setProcessoVij } = useSnackBar()

  const [isRequired, setIsRequired] = useState<boolean>(true)

  useEffect(() => {
    if (dadosProcessuais) {
      reset(dadosProcessuais)
    }
  }, [dadosProcessuais])

  useEffect(() => {
    if (
      (processoVemseWatch == null || !processoVemseWatch) &&
      (processoVijWatch == null || !processoVijWatch)
    ) {
      setIsRequired(true)
    } else {
      setIsRequired(false)
    }
  }, [processoVemseWatch, processoVijWatch])

  useEffect(() => {
    const storedProcessoVij = localStorage.getItem('processoVij')
    if (storedProcessoVij) {
      const parsedValue = JSON.parse(storedProcessoVij)
      setProcessoVij(parsedValue)
      setValue('processoVij', parsedValue)
    }
  }, [])

  const handleSalvar = async (data?: SocioeducationMeasureProceduralDataType) => {
    await handleSalvarMeioAberto(data)
    return Promise.resolve()
  }

  const onError = (errors: FieldErrors<SocioeducationMeasureProceduralDataType>) => {
    const recursiveSetError = (errors: any, parentPath = '') => {
      clearErrors()
      Object.entries(errors).forEach(([key, error]: any) => {
        const path = parentPath ? `${parentPath}.${key}` : key

        if (error?.ref) {
          // Se houver uma referência (ref), usamos o nome da referência para definir o erro
          setError(error.ref.name, { type: error.type, message: error.message })
        } else if (typeof error === 'object') {
          // Recursivamente percorre os objetos internos
          recursiveSetError(error, path)
        }
      })
    }

    recursiveSetError(errors)
    return Promise.reject(errors)
  }

  formRefs.current[currentStep] = {
    submit: async () => {
      if (isDirty && hasDirtyFields && handleHasPermission(Funcionalidades.meioAberto.editar)) {
        await handleSubmit(handleSalvar, onError)()
      }
      return Promise.resolve()
    },
  }
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleSalvar, onError)}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12}>
            <Typography variant={'body1'} color={'secondary'}>
              <strong>DADOS PROCESSUAIS</strong>
            </Typography>
          </Grid>
          <Grid item md={6} xs={12}>
            <InputMask
              control={control}
              name={'processoVij'}
              label={'Processo de Conhecimento (VIJ)'}
              mask='0000000.00.0000.0.00.0000'
              placeholder='Informe o Número do Processo de Conhecimento (VIJ)'
              disabled={doNotHasEditPermission}
              autoComplete='off'
              required={isRequired}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <InputMask
              control={control}
              name={'processoVemse'}
              label={'Processo de Execução (VEMSE)'}
              mask='0000000.00.0000.0.00.0000'
              placeholder='Informe o Número do Processo de Execução (VEMSE)'
              disabled={doNotHasEditPermission}
              autoComplete='off'
              required={isRequired}
            />
          </Grid>
          <Grid item xs={12}>
            <form>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12}>
                  <FormControlRadioBoolean
                    control={control}
                    name='remissao.trataRemissao'
                    label='Trata-se de uma Remissão?'
                    options={simNaoOptions}
                    disabled={doNotHasEditPermission}
                  />
                </Grid>
                {remissaoWatch ? (
                  <Grid item xs={12} marginTop={-2}>
                    <FormControlRadio
                      control={control}
                      name='remissao.tipoRemissao'
                      setValue={setValue}
                      label=''
                      options={tipoRemissaoOptions}
                      disabled={doNotHasEditPermission}
                    />
                  </Grid>
                ) : (
                  ''
                )}

                <Grid item xs={12}>
                  <Typography variant={'body1'}>
                    <strong>Medida Aplicada</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlRadio
                    disabled={doNotHasEditPermission}
                    control={control}
                    name='tipoMedidaSocioeducativa'
                    setValue={setValue}
                    label='Medida Socioeducativa Aplicada:'
                    options={medidaAplicadaOptions}
                  />
                </Grid>
                <Grid item xs={12}>
                  <StatusMeasureList
                    disabled={doNotHasEditPermission}
                    typeOptions={measureStatusOptions}
                    typeOptionsLaPsc={measureStatusLaPscOptions}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlRadio
                    control={control}
                    setValue={setValue}
                    name='medidaRestritiva'
                    label='Cumprimento de Medidas Restritivas de Liberdade Anteriores:'
                    options={medidaRestritivaOptions}
                    disabled={doNotHasEditPermission}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlRadioBoolean
                    control={control}
                    name='reinfracao'
                    label='Reinfração (quando tiver cometido de novo ato infracional):'
                    options={simNaoOptions}
                    disabled={doNotHasEditPermission}
                  />
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
